import setFieldData from "final-form-set-field-data";
import React, { useState } from "react";
import { Field, Form } from "react-final-form";
import { Row, Col, Card, FormGroup } from "reactstrap";
import { DetailedErrorAlert } from "../components/DetailedErrorAlert";
import { DateInput } from "../components/inputs/final_form/DateInput";
import { DropzoneInput } from "../components/inputs/final_form/DropzoneInput";
import { SelectInput } from "../components/inputs/final_form/SelectInput";
import { TextInput } from "../components/inputs/final_form/TextInput";
import { CptSelector } from "../components/inputs/final_form/CptSelector";
import { Icd10Selector } from "../components/inputs/final_form/Icd10Selector";
import { TestSelector } from "../components/inputs/final_form/TestSelector";
import { WarningEngine, WarningField } from "../components/inputs/final_form/WarningEngine";
import { LoadingButton } from "../components/LoadingButton";
import { api, redirectTo, extractErrorMessage } from "../util/api";
import { isBlank } from "../util/helpers";
import { Contact, Hospital, Paperclip, Pill } from "lucide-react";

const warningValidator = (values) => {
  const warnings = {};

  const generateBlankWarning = (field) => `${field} must be filled to resolve missing information`;

  if (isBlank(values.request_patient?.first_name)) {
    warnings["request_patient[first_name]"] = generateBlankWarning("First Name");
  }

  if (isBlank(values.request_patient?.last_name)) {
    warnings["request_patient[last_name]"] = generateBlankWarning("Last Name");
  }

  if (isBlank(values.request_patient?.dob)) {
    warnings["request_patient[dob]"] = generateBlankWarning("Date of Birth");
  }

  if (isBlank(values.request_patient?.gender)) {
    warnings["request_patient[gender]"] = generateBlankWarning("Gender");
  }

  if (isBlank(values.primary_request_insurance?.memberid)) {
    warnings["primary_request_insurance[memberid]"] = generateBlankWarning("Member Id");
  }

  if (isBlank(values.request_provider?.npi)) {
    warnings["request_provider[npi]"] = generateBlankWarning("Provider NPI");
  }

  if (isBlank(values.case?.pa_request?.fixed_verified_dos)) {
    warnings["case[pa_request][fixed_verified_dos]"] = generateBlankWarning("Date of Service");
  }

  if (isBlank(values.case?.verified_test_ids)) {
    warnings["case[verified_test_ids]"] = generateBlankWarning("Tests");
  }

  if (isBlank(values.case?.verified_icd10_codes)) {
    warnings["case[verified_icd10_codes]"] = generateBlankWarning("ICD10 Codes");
  }

  if (isBlank(values.case?.verified_cpt_codes)) {
    warnings["case[verified_cpt_codes]"] = generateBlankWarning("CPT Codes");
  }

  return warnings;
};

const SectionHeader = ({ Icon, children }) => {
  return (
    <h5 className="mb-2 d-flex flex-row align-items-center">
      <span className="me-2">
        <Icon />
      </span>
      <span>{children}</span>
    </h5>
  );
};

export const LabFixForm = ({ initialCaseData, initialTestOptions, initialIcd10CodeOptions, caseInMissingInfo, labId }) => {
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const onSave = async (formData, submit = false) => {
    setError(null);
    setSaving(true);

    try {
      const { NewSupplementalDocuments, ...rest } = formData;
      await api.patch("", { ...rest, submit, supplementalDocuments: { newFiles: NewSupplementalDocuments } });

      if (submit) {
        redirectTo(`/cases/${initialCaseData.public_id}`);
      } else {
        window.location.reload();
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = async (formData) => {
    return await onSave(formData, true);
  };

  return (
    <>
      <DetailedErrorAlert error={error} className="mb-3" />

      <Form onSubmit={onSubmit} mutators={{ setFieldData }} initialValues={initialCaseData}>
        {({ handleSubmit, form, submitting, values }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <SectionHeader Icon={Contact}>Patient Info</SectionHeader>
              <Card className="mb-3 p-3 pb-0">
                <Row>
                  <Col>
                    <WarningField
                      label="First Name"
                      required
                      name="request_patient[first_name]"
                      component={TextInput}
                      type="text"
                      placeholder="First Name"
                    />
                  </Col>

                  <Col>
                    <WarningField
                      label="Last Name"
                      required
                      name="request_patient[last_name]"
                      component={TextInput}
                      type="text"
                      placeholder="Last Name"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <WarningField
                      label="Date of Birth"
                      required
                      name="request_patient[dob]"
                      component={DateInput}
                      placeholder="Date of Birth"
                    />
                  </Col>

                  <Col>
                    <WarningField
                      label="Gender"
                      required
                      name="request_patient[gender]"
                      component={SelectInput}
                      options={[
                        { label: "Male", value: "M" },
                        { label: "Female", value: "F" },
                      ]}
                      placeholder="Gender"
                    />
                  </Col>

                  <Col>
                    <WarningField
                      label="Member ID"
                      required
                      name="primary_request_insurance[memberid]"
                      component={TextInput}
                      type="text"
                      placeholder="Member ID"
                    />
                  </Col>
                </Row>
              </Card>

              <SectionHeader Icon={Hospital}>Provider Info</SectionHeader>
              <Card className="mb-3 p-3 pb-0">
                <Row className="w-50">
                  <Col>
                    <WarningField
                      label="Provider NPI"
                      required
                      name="request_provider[npi]"
                      component={TextInput}
                      type="text"
                      placeholder="Provider NPI"
                    />
                  </Col>
                </Row>
              </Card>

              <SectionHeader Icon={Pill}>Tests and Codes</SectionHeader>
              <Card className="mb-3 p-3 pb-0">
                <Row>
                  <WarningField
                    label="Date of Service"
                    required
                    name="case[pa_request_attributes][fixed_verified_dos]"
                    component={DateInput}
                    placeholder="Date of Service"
                  />
                </Row>
                <Row>
                  <Col>
                    <WarningField
                      label="Tests"
                      required
                      name="case[verified_test_ids]"
                      component={TestSelector}
                      labsId={[labId]}
                      initialOptions={initialTestOptions}
                      isMulti
                      type="text"
                    />

                    <WarningField
                      label="ICD-10 Codes"
                      required
                      name="case[verified_icd10_codes]"
                      component={Icd10Selector}
                      initialOptions={initialIcd10CodeOptions}
                      type="text"
                    />
                  </Col>

                  <Col>
                    <WarningField label="CPT Codes" required name="case[verified_cpt_codes]" component={CptSelector} type="text" />
                  </Col>
                </Row>
              </Card>

              <SectionHeader Icon={Paperclip}>New Supplemental Documents</SectionHeader>
              <Card className="mb-3 p-3 pb-0">
                {/*
                  This explicitly does not exist in the initial values because it is
                  exclusively for uploading new files.
                */}
                <Field name="NewSupplementalDocuments" component={DropzoneInput} multiple />
              </Card>

              <FormGroup>
                <div className="d-flex align-items-center justify-end">
                  <LoadingButton
                    loading={saving}
                    onClick={() => onSave(values, false)}
                    outline
                    disabled={submitting || saving}
                    className="me-2"
                  >
                    Save
                  </LoadingButton>

                  {caseInMissingInfo && (
                    <LoadingButton loading={submitting} type="submit" color="primary" disabled={submitting || saving} className="me-2">
                      Re-submit for Processing
                    </LoadingButton>
                  )}
                </div>
              </FormGroup>

              {caseInMissingInfo && <WarningEngine mutators={form.mutators} validate={warningValidator} />}
            </form>
          );
        }}
      </Form>
    </>
  );
};
